'use client';
import { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TechSpecsTabs } from './TechSpecsTabs';
import Link from 'next/link';

const STAR_COLOR = '#f59e0b';

const MiniStars = ({ rating = 0 }) => {
    const filled = Math.round(rating);
    return (
        <div style={{ display: 'flex', gap: '2px' }}>
            {[1, 2, 3, 4, 5].map(s => (
                <i key={s}
                    className={`pi ${s <= filled ? 'pi-star-fill' : 'pi-star'}`}
                    style={{ fontSize: '0.65rem', color: s <= filled ? STAR_COLOR : '#d1d5db' }}
                />
            ))}
        </div>
    );
};

const SupportFallbackCard = ({ title, icon }) => (
    <div style={{
        border: '1.5px dashed #cbd5e1',
        borderRadius: '12px',
        padding: '1.5rem 1.25rem',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.75rem',
        textAlign: 'center',
    }}>
        <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(11,49,99,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <i className={`pi ${icon}`} style={{ fontSize: '1.2rem', color: 'var(--primary-color)' }} />
        </div>
        <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '0.3rem' }}>
                {title} Not Available Yet
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6, maxWidth: '26rem' }}>
                We're still updating the {title.toLowerCase()} for this product.
                Our support team can help you with any specific questions.
            </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center', marginTop: '0.25rem' }}>
            <Link href="/support" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                background: 'var(--primary-color)', color: '#fff',
                padding: '0.45rem 1rem', borderRadius: '8px',
                fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
            }}>
                <i className="pi pi-headphones" style={{ fontSize: '0.75rem' }} />
                Contact Support
            </Link>
            <Link href="/warranty" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                background: '#fff', color: 'var(--primary-color)',
                padding: '0.45rem 1rem', borderRadius: '8px',
                fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                border: '1.5px solid var(--primary-color)',
            }}>
                <i className="pi pi-shield" style={{ fontSize: '0.75rem' }} />
                Warranty Info
            </Link>
            <a href="mailto:support@ennixelectronics.com" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                background: '#f0fdfa', color: '#0f766e',
                padding: '0.45rem 1rem', borderRadius: '8px',
                fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                border: '1.5px solid #99f6e4',
            }}>
                <i className="pi pi-envelope" style={{ fontSize: '0.75rem' }} />
                Email Us
            </a>
        </div>
    </div>
);

export const TechDetailsSection = ({ productData }) => {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const techDetails = productData?.techDetails || [];
    const preview = techDetails.slice(0, 5);
    const hasMore = techDetails.length > 5;
    const coverImage = productData?.coverImage;
    const productName = productData?.title;
    const avgRating = productData?.averageRating || 0;
    const reviewCount = productData?.reviewCount || 0;

    const hasTech = techDetails.length > 0
        || productData?.warranty
        || productData?.app_supported?.length > 0
        || productData?.Work_With?.length > 0;

    return (
        <>
            {/* ── Full-spec sidebar ─────────────────────────────── */}
            {mounted && (
                <Sidebar
                    visible={open}
                    onHide={() => setOpen(false)}
                    position="right"
                    style={{ width: '520px', maxWidth: '96vw' }}
                    header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '8px',
                                background: 'var(--primary-color)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className="pi pi-list" style={{ fontSize: '0.75rem', color: '#fff' }} />
                            </div>
                            <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>
                                Technical Specification
                            </span>
                        </div>
                    }
                    pt={{
                        header: { style: { background: 'var(--surface-100, #f8fafc)', borderBottom: '1px solid var(--surface-200, #e2e8f0)', padding: '12px 16px' } },
                        content: { style: { padding: 0 } },
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* Product hero */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '14px',
                            padding: '14px 16px',
                            background: 'linear-gradient(135deg, #f0fdfa 0%, #fff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                        }}>
                            {coverImage ? (
                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '12px',
                                    background: '#fff', border: '1.5px solid #99f6e4',
                                    flexShrink: 0, overflow: 'hidden', padding: '4px',
                                    boxShadow: '0 4px 12px rgba(27,184,167,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <img src={coverImage} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                            ) : (
                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '12px',
                                    background: 'var(--primary-color)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    boxShadow: '0 4px 12px rgba(11,49,99,0.22)',
                                }}>
                                    <i className="pi pi-box" style={{ color: '#fff', fontSize: '1.5rem' }} />
                                </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    margin: '0 0 7px', fontWeight: 700, fontSize: '0.85rem',
                                    color: 'var(--primary-color)',
                                    display: '-webkit-box', WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {productName}
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {avgRating > 0 && (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                                            background: 'rgba(27,184,167,0.1)', color: '#1bb8a7',
                                            border: '1px solid rgba(27,184,167,0.3)', borderRadius: '20px',
                                            padding: '3px 10px', fontSize: '0.7rem', fontWeight: 800,
                                        }}>
                                            <MiniStars rating={avgRating} />
                                            {Number(avgRating).toFixed(1)}
                                        </span>
                                    )}
                                    {reviewCount > 0 && (
                                        <span style={{
                                            background: '#f1f5f9', color: '#64748b',
                                            border: '1px solid #e2e8f0', borderRadius: '20px',
                                            padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600,
                                        }}>
                                            {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Spec count chip */}
                        {techDetails.length > 0 && (
                            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    background: 'rgba(11,49,99,0.06)', color: 'var(--primary-color)',
                                    border: '1px solid rgba(11,49,99,0.15)', borderRadius: '8px',
                                    padding: '4px 12px', fontSize: '0.72rem', fontWeight: 700,
                                }}>
                                    <i className="pi pi-table" style={{ fontSize: '0.65rem' }} />
                                    {techDetails.length} Specifications
                                </span>
                            </div>
                        )}

                        {/* Scrollable body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                            <TechSpecsTabs productData={productData} />
                        </div>
                    </div>
                </Sidebar>
            )}

            {/* ── Section header row ─────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
                padding: '0.5rem 0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                        width: '4px', height: '1.4rem',
                        background: 'var(--primary-color)',
                        borderRadius: '2px', flexShrink: 0,
                    }} />
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary-color)' }}>
                        Technical Details
                    </span>
                </div>
                {mounted && hasTech && (
                    <button
                        onClick={() => setOpen(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.45rem',
                            background: 'transparent',
                            border: '1.5px solid rgba(27,184,167,0.5)',
                            borderRadius: '8px', cursor: 'pointer',
                            color: 'var(--secondary-color)', fontSize: '0.8rem', fontWeight: 700,
                            padding: '0.45rem 0.9rem', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f0fdfa'; e.currentTarget.style.borderColor = '#1bb8a7'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(27,184,167,0.5)'; }}
                    >
                        <i className="pi pi-list" style={{ fontSize: '0.75rem' }} />
                        View All Specs
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                    </button>
                )}
            </div>

            {/* ── 5-row preview table or fallback ───────────── */}
            {techDetails.length > 0 ? (
                <div className="mt-3">
                    <DataTable value={preview} stripedRows>
                        <Column field="title" header="Specification" />
                        <Column field="value" header="Details" />
                    </DataTable>
                </div>
            ) : (
                <div className="mt-3">
                    <SupportFallbackCard title="Technical Details" icon="pi-list" />
                </div>
            )}

            {/* ── View-more button at bottom ─────────────────── */}
            {hasMore && mounted && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button
                        onClick={() => setOpen(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.45rem',
                            background: '#f0fdf9', border: '1px solid #99f6e4',
                            borderRadius: '6px', cursor: 'pointer',
                            color: '#0f766e', fontSize: '0.82rem', fontWeight: 600,
                            padding: '0.35rem 0.85rem',
                        }}
                    >
                        View All Specifications
                        <span style={{
                            background: '#ccfbf1', borderRadius: '999px',
                            padding: '0.05rem 0.5rem',
                            fontSize: '0.72rem', fontWeight: 700, color: '#0f766e',
                        }}>
                            +{techDetails.length - 5}
                        </span>
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                    </button>
                </div>
            )}

            {/* ── Group images ───────────────────────────────── */}
            {productData?.groupImages && (
                <div className="grid image-view mt-4">
                    {productData.groupImages.map((image, i) => (
                        <div key={i} className="col">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={image} className="w-full" alt="product" style={{ maxHeight: '300px' }} />
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};
